"use strict";
/*
 * Integration App React package
 * {@link https://www.npmjs.com/package/@integration-app/react}
 * v2.22.1
 */
'use client';

import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, Children } from 'react';
import { clsx } from 'clsx';
import { css } from '../../styled-system/css/css.mjs';
import '../../styled-system/helpers.mjs';
import { sva } from '../../styled-system/css/sva.mjs';
import { Tooltip } from '../Tooltip/index.mjs';

const dataBuilderTitleRecipe = sva({
  className: "data-builder-title",
  slots: ["root", "left-side", "right-side", "title", "title-wrap"],
  base: {
    root: {
      display: "flex",
      flexDir: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "2",
      w: "full",
      maxW: "full"
    },
    "left-side": {
      display: "flex",
      flexDir: "row",
      alignItems: "center",
      flex: "1"
    },
    "right-side": {
      display: "flex",
      flexDir: "row",
      alignItems: "center",
      gap: "1",
      h: "full"
    },
    title: {
      truncate: true,
      flex: "1",
      maxW: "fit-content",
      whiteSpace: "nowrap",
      fontWeight: "medium"
    },
    "title-wrap": {
      whiteSpace: "normal"
    }
  }
});
const Title = forwardRef(
  ({
    children,
    required,
    hasValue,
    hasError,
    hasNameConflict,
    leftSlot,
    typeSelectorSlot,
    rightSlot,
    childrenWrap = false,
    ...props
  }, ref) => {
    const styles = dataBuilderTitleRecipe();
    const childrenAsText = Children.toArray(children).reduce((acc, el) => {
      return acc + (typeof el === "string" ? el : "");
    }, "");
    const showRequiredDot = required && !hasError && !hasValue;
    const hasRightSlot = rightSlot || hasError || showRequiredDot;
    return /* @__PURE__ */ jsxs("div", { className: styles.root, ...props, ref, children: [
      /* @__PURE__ */ jsxs("div", { className: styles["left-side"], children: [
        leftSlot && /* @__PURE__ */ jsx("div", { children: leftSlot }),
        /* @__PURE__ */ jsx("span", { className: clsx(styles.title, childrenWrap && styles["title-wrap"]), title: childrenAsText, children }),
        rightSlot && /* @__PURE__ */ jsx(Fragment, { children: typeSelectorSlot })
      ] }),
      hasRightSlot && /* @__PURE__ */ jsxs("div", { className: styles["right-side"], children: [
        rightSlot && /* @__PURE__ */ jsx(Fragment, { children: rightSlot }),
        hasError && /* @__PURE__ */ jsx(Dot, { reason: "Field has error" }),
        hasNameConflict && /* @__PURE__ */ jsx(Dot, { reason: "Current field name is already used" }),
        showRequiredDot && /* @__PURE__ */ jsx(Dot, { reason: "Field is required" })
      ] })
    ] });
  }
);
Title.displayName = "Title";
const dotRecipe = sva({
  className: "data-builder-title-dot",
  slots: ["root", "dot"],
  base: {
    root: {
      w: "4",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    dot: {
      flexShrink: "0",
      flexGrow: "0",
      w: "2",
      h: "2",
      bg: "tomato.10",
      rounded: "full",
      display: "inline-block",
      cursor: "help"
    }
  }
});
const Dot = forwardRef(({ reason, className, ...rest }, ref) => {
  const styles = dotRecipe();
  return /* @__PURE__ */ jsx(Tooltip, { tooltip: reason, delayToShow: 0, usePortal: true, children: /* @__PURE__ */ jsxs("div", { className: clsx(styles.root, className), children: [
    /* @__PURE__ */ jsx("span", { className: styles.dot, ...rest, ref }),
    /* @__PURE__ */ jsx("span", { className: css({ srOnly: true }), children: reason })
  ] }) });
});

export { Dot, Title, dataBuilderTitleRecipe, dotRecipe };
